import { Navigate, RouteObject } from 'react-router-dom';
import { lazy } from 'react';
import { RootLayout } from '@app/layouts/RootLayout';
import ErrorPage from '@pages/ErrorPage';
import Loadable from '@/shared/common/Loadable';
import { AuthGuard } from './guards/AuthGuard';
import { OsiSelectedGuard } from './guards/OsiSelectedGuard';
import { RoleSelectedGuard } from './guards/RoleSelectedGuard';
import * as paths from './paths';

// Layouts

const HomeLayout = Loadable(lazy(() => import('@app/layouts/home')));
const CabinetLayout = Loadable(lazy(() => import('@app/layouts/cabinet')));
const OsiLayout = Loadable(lazy(() => import('@app/layouts/osi')));
const AppartmentLayout = Loadable(lazy(() => import('@app/layouts/appartment')));
const LogoOnlyLayout = Loadable(lazy(() => import('@app/layouts/LogoOnlyLayout')));

// IMPORT COMPONENTS
// Main
const HomePage = Loadable(lazy(() => import('../../pages/home/index')));
const NotFound = Loadable(lazy(() => import('@pages/Page404')));
const Error = Loadable(lazy(() => import('@pages/Page500')));
const Maintenance = Loadable(lazy(() => import('@pages/Maintenance')));

// Auth
const Register = Loadable(lazy(() => import('@pages/auth/Register')));
const ResetPassword = Loadable(lazy(() => import('@pages/auth/ResetPassword')));
const SelectRole = Loadable(lazy(() => import('@pages/auth/SelectRole')));
const CheckContactPage = Loadable(lazy(() => import('@pages/auth/check-contact')));
const Login = Loadable(lazy(() => import('@pages/auth/Login')));

// Cabinet
const CabinetRoot = Loadable(lazy(() => import('@pages/cabinet/ui')));
const OsiRegistration = Loadable(lazy(() => import('../../pages/cabinet/Registration')));
const UserProfile = Loadable(lazy(() => import('@pages/user/profile')));

// Osi
const OsiRoot = Loadable(lazy(() => import('../../pages/osi/root/ui')));
const OsiInfo = Loadable(lazy(() => import('../../pages/osi/infoV2')));
const OsiAbonents = Loadable(lazy(() => import('@pages/osi/abonents')));
const OsiAccruals = Loadable(lazy(() => import('@pages/osi/accruals')));
const OsiPayments = Loadable(lazy(() => import('@pages/osi/payments')));
const OsiOsv = Loadable(lazy(() => import('@pages/osi/osv')));
const OsiActs = Loadable(lazy(() => import('../../pages/osi/acts')));
const OsiWizard = Loadable(lazy(() => import('../../pages/osi/wizard')));
const OsiDebts = Loadable(lazy(() => import('@pages/osi/debts')));
const OsiInvoices = Loadable(lazy(() => import('@pages/osi/invoices')));
const OsiReports = Loadable(lazy(() => import('@pages/osi/reports')));

// Appartment
const AppartmentRoot = Loadable(lazy(() => import('@pages/appartment/root')));
const AppartmentOsiInfo = Loadable(lazy(() => import('@pages/appartment/osi-info')));
const AppartmentOsv = Loadable(lazy(() => import('@pages/appartment/osv')));
const AppartmentReports = Loadable(lazy(() => import('@pages/appartment/reports')));
const AppartmentInvoices = Loadable(lazy(() => import('@pages/appartmentInvoices')));

const { PATH_CABINET, PATH_APPARTMENT, PATH_OSI, PATH_HOME, PATH_AUTH } = paths;

const authRoutes: RouteObject = {
  path: PATH_AUTH.root,
  children: [
    {
      path: PATH_AUTH.login,
      Component: Login
    },
    {
      path: PATH_AUTH.loginDev,
      Component: Login
    },
    {
      path: PATH_AUTH.register,
      Component: Register
    },
    { path: PATH_AUTH.resetPassword, Component: ResetPassword },
    { path: PATH_AUTH.selectRole, Component: SelectRole },
    { path: PATH_AUTH.checkContact, Component: CheckContactPage }
  ]
};

const cabinetRoutes: RouteObject = {
  path: PATH_CABINET.root,
  Component: CabinetLayout,
  children: [
    { path: PATH_CABINET.root, Component: CabinetRoot },
    {
      path: PATH_CABINET.registration,
      Component: OsiRegistration
    },
    {
      path: PATH_CABINET.user,
      Component: UserProfile
    }
  ]
};

const osiRoutes: RouteObject = {
  path: PATH_OSI.root,
  Component: OsiLayout,
  children: [
    { path: PATH_OSI.root, Component: OsiRoot },
    {
      path: PATH_OSI.info,
      Component: OsiInfo
    },
    {
      path: PATH_OSI.abonents,
      Component: OsiAbonents
    },
    {
      path: PATH_OSI.accruals,
      Component: OsiAccruals
    },
    {
      path: PATH_OSI.payments,
      Component: OsiPayments
    },
    {
      path: PATH_OSI.osv,
      Component: OsiOsv
    },
    {
      path: PATH_OSI.acts,
      Component: OsiActs
    },
    {
      path: PATH_OSI.wizard,
      Component: OsiWizard
    },
    {
      path: PATH_OSI.debts,
      Component: OsiDebts
    },
    {
      path: PATH_OSI.invoices,
      Component: OsiInvoices
    },
    {
      path: PATH_OSI.reports,
      Component: OsiReports
    }
  ]
};

const apartmentRoutes: RouteObject = {
  path: PATH_APPARTMENT.root,
  Component: AppartmentLayout,
  children: [
    { path: PATH_APPARTMENT.root, Component: AppartmentRoot },
    {
      path: PATH_APPARTMENT.osi,
      Component: AppartmentOsiInfo
    },
    {
      path: PATH_APPARTMENT.osv,
      Component: AppartmentOsv
    },
    {
      path: PATH_APPARTMENT.reports,
      Component: AppartmentReports
    },
    {
      path: PATH_APPARTMENT.invoices,
      Component: AppartmentInvoices
    }
  ]
};

const mainRoutes: RouteObject = {
  path: '*',
  Component: LogoOnlyLayout,
  children: [
    { path: 'maintenance', Component: Maintenance },
    { path: '404', Component: NotFound },
    { path: '500', Component: Error },
    { path: '*', element: <Navigate to="/404" replace /> }
  ]
};

const homeRoutes: RouteObject = {
  path: PATH_HOME.root,
  Component: HomeLayout,
  children: [{ path: '/', Component: HomePage }]
};

/**
 * Авторизованный периметр (OSI-806).
 *
 * Один pathless-роут с `<AuthGuard/>` в качестве element, внутри — все закрытые зоны:
 * `/osi` (со всеми вкладками), `/cabinet`, `/appartment`.
 *
 * ПОЧЕМУ ИМЕННО pathless-РОУТ, А НЕ ОБЁРТКА ВОКРУГ КАЖДОГО LAYOUT'А:
 * 1. Проверка объявлена ОДИН раз — нельзя забыть обернуть новую зону/вкладку: любой роут,
 *    добавленный в `children` этого узла, автоматически закрыт.
 * 2. Не ломается вложенность layout'ов: у зон сохраняются их `path` + `Component`
 *    (OsiLayout / CabinetLayout / AppartmentLayout), гвард лишь добавляет один уровень
 *    вложенности и рендерит `<Outlet/>`. Вариант с `element: <AuthGuard><OsiLayout/></AuthGuard>`
 *    потребовал бы заменить `Component` на `element` у трёх зон и продублировать гвард трижды.
 * 3. У pathless-роута нет своего пути, поэтому ранжирование маршрутов не меняется:
 *    `/osi/*`, `/cabinet/*`, `/appartment/*` матчатся ровно как раньше, catch-all `mainRoutes`
 *    ('*' → /404) по-прежнему проигрывает конкретным путям.
 * 4. `errorElement` остаётся на корневом роуте и продолжает ловить ошибки всех потомков
 *    (в react-router ошибки всплывают до ближайшего errorElement) — отдельный ему не нужен.
 *
 * НЕ ЗАКРЫТЫ СОЗНАТЕЛЬНО: лендинг '/', весь `/auth/*`, служебные /404, /500, /maintenance.
 * Нотариат живёт на отдельной сессии (ключ `notary:token`, см. shared/api/session.ts) и своих
 * роутов в этом конфиге не имеет, поэтому под этот гвард не попадает.
 */
/**
 * Раздел ОСИ под вторым гвардом (OSI-806): авторизован, но дом не выбран → /cabinet.
 * Обёрнут ВЕСЬ раздел, а не только корень: вкладки (/osi/info, /osi/abonents, /osi/accruals,
 * /osi/osv, /osi/payments, /osi/reports, /osi/invoices, /osi/debts, /osi/acts) висли вечным
 * спиннером по той же причине — у каждой ранний `return <LoadingScreen/>` по отсутствию osiInfo.
 * Гвард ставим вокруг `osiRoutes` (снаружи OsiLayout), поэтому он отрабатывает до монтирования
 * layout'а раздела; `/cabinet` и `/appartment` этой проверки не требуют — дом там не нужен.
 */
const osiSectionRoutes: RouteObject = {
  element: <OsiSelectedGuard />,
  children: [osiRoutes]
};

/**
 * Второй уровень авторизованного периметра: «роль выбрана?» (дефект «сброс роли на ABONENT
 * после очистки сессии», см. guards/RoleSelectedGuard.tsx). Токен есть, а роль не выбрана →
 * автовыбор единственной роли или редирект на /auth/select-role.
 *
 * Стоит ВНУТРИ AuthGuard (без токена до него не доходит) и СНАРУЖИ OsiSelectedGuard: сначала
 * нужна роль, и только потом имеет смысл проверять выбранный дом. Под ним все закрытые зоны —
 * /cabinet (вкл. /cabinet/user, /cabinet/registration), /osi/*, /appartment/*.
 * `/auth/*` (в т.ч. сам /auth/select-role), лендинг и служебные страницы под ним НЕ лежат —
 * поэтому петли редиректов нет.
 */
const roleSelectedRoutes: RouteObject = {
  element: <RoleSelectedGuard />,
  children: [cabinetRoutes, osiSectionRoutes, apartmentRoutes]
};

const protectedRoutes: RouteObject = {
  element: <AuthGuard />,
  children: [roleSelectedRoutes]
};

const maintenanceMode = process.env.VITE_REACT_APP_MAINTENANCE_MODE === 'true';

export const routesConfig: RouteObject[] = [
  {
    errorElement: <ErrorPage />,
    Component: RootLayout,
    children: maintenanceMode
      ? [
          {
            path: '/maintenance',
            Component: Maintenance
          },
          {
            path: '*',
            element: <Navigate to="/maintenance" replace />
          }
        ]
      : // OSI-806: закрытые зоны (/osi, /cabinet, /appartment) теперь идут одним узлом под AuthGuard,
        // публичные (/auth/*, служебные, лендинг) остаются на верхнем уровне без изменений.
        [authRoutes, protectedRoutes, mainRoutes, homeRoutes]
  }
];
